import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UsersRound, Plus, Pencil, Mail, Phone, IdCard, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Teachers() {
  const { scopeParams, selectedSchoolId, isSuperadmin, user } = useAuth();
  const [teachers, setTeachers] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const canEdit = ["SUPERADMIN", "DIRECAO", "COORDENACAO"].includes(user.role);
  const fetchAll = () => {
    api.get("/teachers", { params: scopeParams }).then((r) => setTeachers(r.data));
    api.get("/subjects", { params: scopeParams }).then((r) => setSubjects(r.data));
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [selectedSchoolId]);

  const openNew = () => { setEditing(null); setForm({ name: "", cpf: "", registration: "", email: "", phone: "", subject_ids: [], status: "ATIVO" }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...t, subject_ids: t.subject_ids || [] }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSubj = (id) => setForm((f) => ({ ...f, subject_ids: f.subject_ids.includes(id) ? f.subject_ids.filter((x) => x !== id) : [...f.subject_ids, id] }));
  const resend = async (t) => {
    try {
      const r = await api.post(`/teachers/${t.id}/send-access`);
      if (r.data.access_status === "ENVIADO") toast.success(`Link de primeiro acesso enviado para ${t.email}`);
      else if (r.data.access_status === "EMAIL_EM_USO") toast.warning("Este e-mail já pertence a outro usuário");
      else toast.warning("Não foi possível enviar o e-mail agora");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = editing ? await api.put(`/teachers/${editing.id}`, form) : await api.post("/teachers", form);
      const st = r.data?.access_status;
      const msg = { ENVIADO: "Acesso criado: link de primeiro acesso enviado para o e-mail do professor", FALHA_ENVIO: "Acesso criado, mas o e-mail não pôde ser enviado agora. Use 'Reenviar acesso'.", EMAIL_EM_USO: "Este e-mail já pertence a outro usuário — acesso não criado", SEM_EMAIL: "Salvo. Sem e-mail: o acesso será criado quando o e-mail for cadastrado." }[st];
      if (st === "EMAIL_EM_USO" || st === "FALHA_ENVIO") toast.warning(msg); else toast.success(msg || "Salvo");
      setOpen(false); fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const subjName = (id) => subjects.find((s) => s.id === id)?.name || id;
  if (teachers === null) return <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-blue-700" /></div>;
  const needsSchool = isSuperadmin && !selectedSchoolId;

  return (
    <div>
      <PageHeader title="Professores" subtitle="Corpo docente e atribuições de disciplinas">
        {canEdit && <Button onClick={openNew} disabled={needsSchool} data-testid="teacher-add-button" className="bg-blue-700 hover:bg-blue-800"><Plus className="mr-1.5 h-4 w-4" /> Novo Professor</Button>}
      </PageHeader>
      {teachers.length === 0 ? <EmptyState icon={UsersRound} title="Nenhum professor cadastrado" /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {teachers.map((t) => (
            <Card key={t.id} data-testid="teacher-card" className="card-hover border-slate-200 p-5">
              <div className="flex items-start gap-3">
                <Avatar className="h-12 w-12"><AvatarFallback className="bg-violet-100 font-semibold text-violet-700">{t.name.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-display font-bold text-slate-900">{t.name}</h3>
                  <StatusBadge status={t.status} />
                </div>
              </div>
              <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                {t.registration && <p className="flex items-center gap-2"><IdCard className="h-4 w-4 text-slate-400" /> Mat. {t.registration}</p>}
                {t.email && <p className="flex items-center gap-2 truncate"><Mail className="h-4 w-4 text-slate-400" /> {t.email}</p>}
                {t.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {t.phone}</p>}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(t.subject_ids || []).map((id) => <span key={id} data-testid="teacher-subjects-badge" className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">{subjName(id)}</span>)}
              </div>
              {canEdit && <Button variant="outline" size="sm" onClick={() => openEdit(t)} className="mt-4 w-full"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Editar</Button>}
              {canEdit && t.email && <Button variant="ghost" size="sm" onClick={() => resend(t)} className="mt-1 w-full text-blue-700" data-testid={`teacher-send-access-${t.id}`}><Mail className="mr-1.5 h-3.5 w-3.5" /> Reenviar acesso</Button>}
            </Card>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Editar Professor" : "Novo Professor"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Nome *</Label><Input data-testid="teacher-name-input" value={form.name || ""} onChange={(e) => set("name", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>CPF</Label><Input value={form.cpf || ""} onChange={(e) => set("cpf", e.target.value)} /></div>
              <div><Label>Matrícula func.</Label><Input value={form.registration || ""} onChange={(e) => set("registration", e.target.value)} /></div>
            </div>
            <div><Label>E-mail</Label><Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
            <div><Label>Telefone</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
            <div>
              <Label>Disciplinas</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {subjects.map((s) => (
                  <button key={s.id} type="button" onClick={() => toggleSubj(s.id)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition ${form.subject_ids?.includes(s.id) ? "bg-blue-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                    {s.name}
                  </button>
                ))}
                {subjects.length === 0 && <p className="text-xs text-slate-400">Cadastre disciplinas primeiro.</p>}
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} disabled={saving || !form.name} data-testid="teacher-save-button" className="bg-blue-700 hover:bg-blue-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
